import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAnnoScolastico } from "@/lib/annoScolastico";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";

const DAYS_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

// Estrae il numero di classe (1..5) da stringhe tipo "1A", "3B", "2  A", "1° A"
function classNum(classe) {
  if (!classe) return null;
  const m = String(classe).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}
// gruppo doposcuola primaria: 1=cl 1-2, 2=cl 3-4-5
function classToGruppo(classe) {
  const n = classNum(classe);
  if (n == null) return null;
  if (n <= 2) return 1;
  if (n <= 5) return 2;
  return null;
}

const fmtAnnoScol = (a) => {
  const n = Number(a);
  if (!n) return "—";
  return `${n}/${String(n + 1).slice(-2)}`;
};

// Formatta una data ISO ('2026-04-24T08:02:35.744Z') in formato breve italiano "24/04/26 08:02"
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return "—"; }
}

// Comparatore per ordinare iscrizioni dalla più vecchia alla più recente (per data/ora iscrizione)
function cmpCreatedAt(a, b) {
  const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
  const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
  return da - db;
}

function giorniToList(g) {
  if (!g) return [];
  if (Array.isArray(g)) return g.map(Number).filter(n => !Number.isNaN(n));
  if (typeof g === "string") return g.split(",").map(x => parseInt(x.trim(), 10)).filter(n => !Number.isNaN(n));
  return [];
}

export default function ExtraStatoCorsiPage() {
  const { anno: annoGlobal } = useAnnoScolastico();
  const [annoOpts, setAnnoOpts] = useState([]);
  const [anno, setAnno] = useState(null);
  const [corsi, setCorsi] = useState([]);
  const [iscrizioni, setIscrizioni] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({}); // path → bool
  const [includeChiuse, setIncludeChiuse] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/anni");
        const list = r.data?.anni || [];
        setAnnoOpts(list);
        if (list.length > 0 && anno == null) setAnno(annoGlobal || list[0]);
      } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento anni"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 🆕 Sincronizza al cambio globale AS
  useEffect(() => {
    if (annoGlobal != null && annoGlobal !== anno) setAnno(annoGlobal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoGlobal]);

  const load = async () => {
    if (!anno) return;
    setLoading(true); setErr(null);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/mensa-matrice/corsi", { params: { anno } }),
        api.get("/mensa-matrice/extra-iscrizioni", { params: { anno } }),
      ]);
      setCorsi(r1.data?.items || r1.data || []);
      setIscrizioni(r2.data?.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento dati");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (anno) load(); }, [anno]);

  const iscrizioniAttive = useMemo(() => {
    if (includeChiuse) return iscrizioni;
    return iscrizioni.filter(it => (it.stato_iscrizione || "").toLowerCase() !== "chiusa");
  }, [iscrizioni, includeChiuse]);

  // Albero: corso → giorno → gruppo → iscritti
  const tree = useMemo(() => {
    const out = [];
    const corsiSorted = [...corsi].sort((a, b) => {
      const oa = a.ordinamento || 99999, ob = b.ordinamento || 99999;
      if (oa !== ob) return oa - ob;
      return String(a.codice).localeCompare(String(b.codice));
    });
    for (const c of corsiSorted) {
      const giorniAmm = (c.giorni_ammessi || []).slice().sort((a, b) => a - b);
      // Trova iscrizioni a questo corso
      const iscrCorso = iscrizioniAttive.filter(it => it.corso_codice === c.codice);
      const numGruppi = Number(c.num_gruppi || 0);
      const giorniNode = giorniAmm.map(g => {
        const iscrGiorno = iscrCorso.filter(it => giorniToList(it.giorni).includes(g));
        // Raggruppa per gruppo SOLO se il corso ha più di un gruppo configurato
        // (es. DOPO_EF/Doposcuola Primaria con num_gruppi=2 → classi 1-2 vs 3-4-5).
        // Tutti gli altri corsi (num_gruppi=1) mostrano una sezione unica "Tutti gli iscritti".
        let gruppi;
        if (numGruppi > 1) {
          const groupsMap = new Map();
          for (const it of iscrGiorno) {
            const gr = classToGruppo(it.classe);
            const key = gr == null ? 0 : gr; // 0 = "senza gruppo"
            if (!groupsMap.has(key)) groupsMap.set(key, []);
            groupsMap.get(key).push(it);
          }
          gruppi = [];
          const ordered = [...groupsMap.keys()].sort((a, b) => a - b);
          for (const k of ordered) {
            gruppi.push({
              gruppoNum: k,
              label: k === 0 ? "Senza gruppo (classe non riconosciuta)" : `Gruppo ${k}${k === 1 ? " · classi 1-2" : (k === 2 ? " · classi 3-4-5" : "")}`,
              iscritti: groupsMap.get(k).slice().sort(cmpCreatedAt),
            });
          }
        } else {
          // Nessun raggruppamento per gruppo: una sezione "Tutti gli iscritti"
          gruppi = [{
            gruppoNum: 0,
            label: "Tutti gli iscritti",
            iscritti: iscrGiorno.slice().sort(cmpCreatedAt),
          }];
        }
        return { giorno: g, label: DAYS_LABELS[g] || `giorno ${g}`, gruppi, totale: iscrGiorno.length };
      });
      out.push({
        corso: c,
        codice: c.codice,
        descrizione: c.descrizione || c.codice,
        ordinamento: c.ordinamento || 0,
        giorni: giorniNode,
        totaleIscritti: iscrCorso.length,
        max: Number(c.max_iscritti || 0),
      });
    }
    return out;
  }, [corsi, iscrizioniAttive]);

  const toggle = (k) => setExpanded(e => ({ ...e, [k]: !e[k] }));
  const espandiTutto = () => {
    const all = {};
    for (const c of tree) {
      all[`c:${c.codice}`] = true;
      for (const g of c.giorni) {
        all[`g:${c.codice}:${g.giorno}`] = true;
        for (const gr of g.gruppi) all[`gr:${c.codice}:${g.giorno}:${gr.gruppoNum}`] = true;
      }
    }
    setExpanded(all);
  };
  const chiudiTutto = () => setExpanded({});

  // Apre una nuova finestra con l'intero albero espanso, formattato per la stampa:
  // - una pagina per ogni combinazione (corso, giorno, gruppo)
  // - alunni ordinati per data/ora iscrizione (più vecchia → più recente)
  // - intestazione e tabella con colonna data/ora iscrizione
  const stampaAlbero = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]));
    const annoLabel = fmtAnnoScol(anno);
    const oggi = new Date().toLocaleString("it-IT");
    const sezioni = [];
    for (const c of tree) {
      for (const g of c.giorni) {
        for (const gr of g.gruppi) {
          // Una sezione per ogni (corso × giorno × gruppo). Includiamo anche quelle vuote? No: skip.
          if (gr.iscritti.length === 0) continue;
          const rows = gr.iscritti.map((it, idx) => `
            <tr>
              <td class="num">${idx + 1}</td>
              <td class="strong">${esc((it.cognome || "") + " " + (it.nome || ""))}</td>
              <td class="mono">${esc(it.alunno_cod ?? "—")}</td>
              <td>${esc(it.classe || "—")}</td>
              <td>${esc(it.settore || "—")}</td>
              <td>${esc((it.stato_iscrizione || "pending").toUpperCase())}</td>
              <td class="mono">${esc(fmtDateTime(it.created_at))}</td>
            </tr>`).join("");
          sezioni.push(`
            <section class="page">
              <header class="hdr">
                <div class="title">
                  <span class="cod">${esc(c.codice)}</span>
                  <span class="desc">${esc(c.descrizione)}</span>
                </div>
                <div class="meta">
                  <span class="day">${esc(g.label)}</span>
                  <span class="sep">·</span>
                  <span class="grp">${esc(gr.label)}</span>
                  <span class="sep">·</span>
                  <span class="count">${gr.iscritti.length} iscritti</span>
                </div>
              </header>
              <table class="tbl">
                <thead>
                  <tr>
                    <th>#</th><th>ALUNNO</th><th>COD.</th><th>CLASSE</th><th>SETT.</th><th>STATO</th><th>DATA ISCR.</th>
                  </tr>
                </thead>
                <tbody>${rows}</tbody>
              </table>
              <footer class="ft">canossaHUB · Stato Corsi · A.S. ${esc(annoLabel)} · stampato il ${esc(oggi)}</footer>
            </section>`);
        }
      }
    }
    if (sezioni.length === 0) {
      alert("Nessuna iscrizione da stampare.");
      return;
    }
    const html = `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>Stato Corsi · ${esc(annoLabel)}</title>
<style>
  @page { size: A4; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #222; }
  .page { page-break-after: always; padding: 0; }
  .page:last-child { page-break-after: auto; }
  .hdr { border-bottom: 2px solid #4a148c; padding-bottom: 6px; margin-bottom: 8px; }
  .title { font-size: 18px; font-weight: 800; color: #4a148c; }
  .title .cod { letter-spacing: 1px; margin-right: 8px; }
  .title .desc { color: #222; font-weight: 600; }
  .meta { margin-top: 4px; font-size: 13px; color: #0d47a1; font-weight: 600; }
  .meta .sep { margin: 0 6px; color: #999; }
  .meta .count { background: #ede7f6; color: #4a148c; padding: 1px 8px; border-radius: 3px; }
  .tbl { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 4px; }
  .tbl thead tr { background: #eceff1; color: #37474f; }
  .tbl th, .tbl td { border: 1px solid #cfd8dc; padding: 4px 6px; text-align: left; }
  .tbl th { font-size: 10px; letter-spacing: 0.3px; }
  .tbl td.strong { font-weight: 700; }
  .tbl td.mono { font-family: Consolas, "Courier New", monospace; color: #555; white-space: nowrap; }
  .tbl td.num { text-align: right; width: 30px; color: #666; }
  .ft { margin-top: 8px; font-size: 9px; color: #888; border-top: 1px dashed #ccc; padding-top: 4px; text-align: right; }
</style>
</head><body>
${sezioni.join("\n")}
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="extra-stato-corsi" style={{ padding: "12px 20px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 10, padding: 12, background: "#fafafa", border: "1px solid #e0e0e0" }}>
        <AnnoAttivoSelector />
        <Field label="ISCRIZIONI CHIUSE">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, padding: "6px 10px", border: "1px solid #ccc", borderRadius: 2, background: "white" }}>
            <input data-testid="stato-corsi-chiuse" type="checkbox" checked={includeChiuse} onChange={e => setIncludeChiuse(e.target.checked)} />
            Includi 🔒 chiuse
          </label>
        </Field>
        <button data-testid="stato-corsi-refresh" onClick={load}
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          🔄 Aggiorna
        </button>
        <button data-testid="stato-corsi-expand-all" onClick={espandiTutto}
          style={{ padding: "6px 14px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          ⊞ Espandi tutto
        </button>
        <button data-testid="stato-corsi-collapse-all" onClick={chiudiTutto}
          style={{ padding: "6px 14px", background: "#5d4037", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          ⊟ Chiudi tutto
        </button>
        <button data-testid="stato-corsi-print" onClick={stampaAlbero}
          title="Apre una nuova finestra con l'intero albero espanso, formattato per la stampa: una pagina per ogni combinazione (corso · giorno · gruppo), alunni ordinati per data/ora iscrizione (più vecchia prima)."
          style={{ padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          🖨️ Stampa albero
        </button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "#666" }}>
          {tree.length} corsi · {iscrizioniAttive.length} iscrizioni{includeChiuse ? " (incluse chiuse)" : " attive"}
        </div>
      </div>

      {err && <div style={{ padding: 10, background: "#ffebee", border: "1px solid #ef9a9a", color: "#c62828", fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {loading && <div style={{ padding: 16, color: "#666", fontStyle: "italic" }}>Caricamento…</div>}

      <div style={{ background: "white", border: "1px solid #ddd" }}>
        {!loading && tree.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessun corso configurato per questo anno.</div>
        )}
        {tree.map(corso => (
          <CorsoNode
            key={corso.codice}
            corso={corso}
            expanded={expanded}
            toggle={toggle}
          />
        ))}
      </div>
    </div>
  );
}

function CorsoNode({ corso, expanded, toggle }) {
  const k = `c:${corso.codice}`;
  const open = !!expanded[k];
  const empty = corso.totaleIscritti === 0;
  return (
    <div data-testid={`stato-corso-${corso.codice}`} style={{ borderBottom: "1px solid #e0e0e0" }}>
      <button data-testid={`stato-corso-toggle-${corso.codice}`}
        onClick={() => toggle(k)}
        style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: open ? "#ede7f6" : "white", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
        <span style={{ width: 16, color: "#6a1b9a", fontWeight: 800 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontWeight: 700, color: "#4a148c" }}>{corso.codice}</span>
        <span style={{ color: "#333" }}>{corso.descrizione}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontFamily: "Consolas, monospace", padding: "2px 8px", borderRadius: 2, color: "white",
          background: empty ? "#9e9e9e" : (corso.max > 0 && corso.totaleIscritti >= corso.max ? "#c62828" : "#1565c0") }}>
          {corso.totaleIscritti}{corso.max ? ` / ${corso.max}` : ""} iscritti
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 26, background: "#fcfafd" }}>
          {corso.giorni.length === 0 && (
            <div style={{ padding: 10, color: "#999", fontSize: 12, fontStyle: "italic" }}>Corso senza giorni ammessi configurati.</div>
          )}
          {corso.giorni.map(g => (
            <GiornoNode key={g.giorno} corso={corso} giorno={g} expanded={expanded} toggle={toggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function GiornoNode({ corso, giorno, expanded, toggle }) {
  const k = `g:${corso.codice}:${giorno.giorno}`;
  const open = !!expanded[k];
  return (
    <div style={{ borderTop: "1px dashed #ddd" }}>
      <button data-testid={`stato-giorno-toggle-${corso.codice}-${giorno.giorno}`}
        onClick={() => toggle(k)}
        style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: open ? "#e3f2fd" : "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>
        <span style={{ width: 12, color: "#0d47a1", fontWeight: 800 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontWeight: 700, color: "#0d47a1" }}>{giorno.label}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontFamily: "Consolas, monospace", color: "#0d47a1", padding: "1px 7px", borderRadius: 2, background: "#bbdefb" }}>
          {giorno.totale} iscritti
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 24 }}>
          {giorno.gruppi.map(gr => (
            <GruppoNode key={gr.gruppoNum} corso={corso} giorno={giorno} gruppo={gr} expanded={expanded} toggle={toggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function GruppoNode({ corso, giorno, gruppo, expanded, toggle }) {
  const k = `gr:${corso.codice}:${giorno.giorno}:${gruppo.gruppoNum}`;
  const open = !!expanded[k];
  const bg = gruppo.gruppoNum === 0 ? "#fafafa" : (gruppo.gruppoNum === 1 ? "#fff3e0" : "#f3e5f5");
  const col = gruppo.gruppoNum === 1 ? "#bf360c" : (gruppo.gruppoNum === 2 ? "#4a148c" : "#37474f");
  return (
    <div style={{ borderTop: "1px dotted #d7ccc8" }}>
      <button data-testid={`stato-gruppo-toggle-${corso.codice}-${giorno.giorno}-${gruppo.gruppoNum}`}
        onClick={() => toggle(k)}
        style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "5px 10px", background: open ? bg : "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>
        <span style={{ width: 12, color: col, fontWeight: 800 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontWeight: 700, color: col }}>{gruppo.label}</span>
        <span style={{ marginLeft: "auto", fontSize: 10, fontFamily: "Consolas, monospace", color: col, padding: "1px 6px", borderRadius: 2, background: bg }}>
          {gruppo.iscritti.length} iscritti
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 24, paddingBottom: 6, paddingRight: 10 }}>
          {gruppo.iscritti.length === 0 ? (
            <div style={{ padding: "4px 8px", color: "#999", fontSize: 11, fontStyle: "italic" }}>Nessun iscritto</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "#eceff1", color: "#37474f" }}>
                  <th style={th}>#</th>
                  <th style={th}>ALUNNO</th>
                  <th style={th}>COD.</th>
                  <th style={th}>CLASSE</th>
                  <th style={th}>SETTORE</th>
                  <th style={th}>STATO</th>
                  <th style={th}>DATA ISCR.</th>
                </tr>
              </thead>
              <tbody>
                {gruppo.iscritti.map((it, idx) => (
                  <tr key={it._id || idx} data-testid={`stato-iscr-${corso.codice}-${giorno.giorno}-${gruppo.gruppoNum}-${idx}`} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={td}>{idx + 1}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{(it.cognome || "")} {(it.nome || "")}</td>
                    <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#666" }}>{it.alunno_cod ?? "—"}</td>
                    <td style={td}>{it.classe || "—"}</td>
                    <td style={td}>{it.settore || "—"}</td>
                    <td style={td}>{(it.stato_iscrizione || "pending").toUpperCase()}</td>
                    <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#555", whiteSpace: "nowrap" }}>{fmtDateTime(it.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#37474f", marginBottom: 3, letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}

const th = { padding: "4px 8px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: 0.3 };
const td = { padding: "3px 8px", verticalAlign: "top" };
